<template>
  <div class="dashboard-usage-split">
    <div class="us-header">
      <div>
        <h3 class="us-title">{{ $t('org-dashboard.usage-split') }}</h3>
        <p class="us-subtitle">{{ $t('org-dashboard.usage-split-subtitle') }}</p>
      </div>
      <div class="us-toggle" v-if="viewModes && viewModes.length > 1">
        <v-btn-toggle :value="activeViewMode" @change="$emit('update:activeViewMode', $event)" mandatory dense class="us-btn-toggle">
          <v-btn x-small :value="mode.value" v-for="mode in viewModes" :key="mode.value">{{ mode.label }}</v-btn>
        </v-btn-toggle>
      </div>
    </div>
    <div class="us-body">
      <div class="us-donut-wrap">
        <highcharts :options="chartOptions" ref="chart" />
        <div class="us-donut-center">
          <span class="us-donut-total">{{ formatNumber(internalValue + externalValue) }}</span>
          <span class="us-donut-unit">{{ unit }}</span>
        </div>
      </div>
      <div class="us-legend">
        <div class="us-legend-item">
          <span class="us-legend-dot" style="background-color: #3B82F6"></span>
          <div class="us-legend-info">
            <span class="us-legend-label">{{ $t('org-dashboard.usage-internal') }}</span>
            <span class="us-legend-value">{{ formatNumber(internalValue) }} {{ unit }}</span>
            <span class="us-legend-percent">{{ internalPercent }}%</span>
          </div>
        </div>
        <div class="us-legend-item">
          <span class="us-legend-dot" style="background-color: #F59E0B"></span>
          <div class="us-legend-info">
            <span class="us-legend-label">{{ $t('org-dashboard.usage-external') }}</span>
            <span class="us-legend-value">{{ formatNumber(externalValue) }} {{ unit }}</span>
            <span class="us-legend-percent">{{ externalPercent }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardUsageSplit',
  components: {
    highcharts: Chart,
  },
  props: {
    internalValue: { type: Number, default: 0 },
    externalValue: { type: Number, default: 0 },
    internalPercent: { type: Number, default: 0 },
    externalPercent: { type: Number, default: 0 },
    unit: { type: String, default: 'kWh' },
    viewModes: { type: Array, default: () => [] },
    activeViewMode: { type: String, default: 'kwh' },
  },

  computed: {
    hasData() {
      return this.internalValue > 0 || this.externalValue > 0
    },
    chartOptions() {
      const data = this.hasData
        ? [
            { name: this.$t('org-dashboard.usage-internal'), y: this.internalValue, color: '#3B82F6' },
            { name: this.$t('org-dashboard.usage-external'), y: this.externalValue, color: '#F59E0B' },
          ]
        : [
            { name: this.$t('org-dashboard.no-data'), y: 1, color: '#e5e7eb' },
          ]

      return {
        chart: {
          type: 'pie',
          height: 180,
          width: 180,
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: {
          enabled: this.hasData,
          pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)',
        },
        plotOptions: {
          pie: {
            innerSize: '70%',
            dataLabels: { enabled: false },
            borderWidth: 0,
            states: { hover: { brightness: this.hasData ? -0.05 : 0 } },
          },
        },
        series: [{ data }],
      }
    },
  },

  methods: {
    formatNumber(val) {
      if (val === null || val === undefined) return '-'
      if (typeof val === 'number') return val.toLocaleString()
      return val
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-usage-split {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.us-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.us-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.us-subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.us-btn-toggle .v-btn {
  text-transform: none !important;
  font-size: 10px !important;
  letter-spacing: 0 !important;
  min-width: 40px !important;
}

.us-body {
  display: flex;
  align-items: center;
  gap: 32px;
}

.us-donut-wrap {
  flex-shrink: 0;
  position: relative;
}

.us-donut-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.us-donut-total {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.us-donut-unit {
  font-size: 10px;
  color: #9ca3af;
}

.us-legend {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

.us-legend-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.us-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
  margin-top: 3px;
}

.us-legend-info {
  display: flex;
  flex-direction: column;
}

.us-legend-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.us-legend-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.us-legend-percent {
  font-size: 11px;
  color: #6b7280;
}

@media (max-width: 768px) {
  .us-body {
    flex-direction: column;
  }
}
</style>
